<template>
  <div class="app-step-one-form">
    <resume-order v-if="authStore.isLoggedIn"></resume-order>
    <div class="app-step-one-form-header">
      <div class="app-step-one-form-header__row">
        <div class="app-step-one-form-header__col">
          <h2 v-if="isHome">START BUILDING YOUR RELEASE TODAY!</h2>

          <h2 v-else>
            {{ packageItem?.name }}

            <template v-if="packagePricesCount > 1">
              as low as
              {{ firstPackageItemCredits }}
              for <b>{{ formatPrice(firstPackageItem?.price) }}</b>

              <template v-if="firstPackageItemCredits > 1">
                <b>({{ formatPrice(perReleasePrice) }} per release)</b>
              </template>
            </template>

            <p v-if="packagePricesCount > 1">Bigger discounts below</p>
          </h2>
        </div>
        <div class="app-step-one-form-header__col steps">
          <step-progress theme="light"></step-progress>
        </div>
      </div>
    </div>

    <section
      v-if="route.name !== 'home' && packagePricesCount > 1"
      class="additional-combinations"
    >
      <h2>Additional Combinations</h2>
      <ul>
        <li :key="packagePrice.id" v-for="packagePrice in packagePrices">
          {{ packagePrice.credits }} for
          {{ formatPrice(packagePrice.price) }} ({{
            formatPrice(Math.ceil(packagePrice.price / packagePrice.credits))
          }})
        </li>
      </ul>
    </section>

    <p v-if="isHome" class="app-step-one-form__content">
      Searching for a national press release distribution service that sends
      your press release to the right people, at the right price? With more than
      100,000 opt-in journalists and a relationship with the largest and
      most-respected newswire, we'll get your press release directly to the
      desktops of journalists, freelance writers, and bloggers.
    </p>
    <router-link
      v-if="false"
      :to="{ path: '/' }"
      class="app-step-one-form__notice"
    >
      You have unused distribution credit available. Click here to use your
      press release credit(s).
    </router-link>

    <form
      class="app-step-one-form__form app-step-one-form-form"
      @submit.prevent="handleSubmit"
    >
      <div class="app-step-one-form-form__row">
        <app-input-validator :errors="v$.firstName.$errors">
          <app-input
            v-model="state.firstName"
            label-text="First Name"
            name="First Name"
            theme="default"
          ></app-input>
        </app-input-validator>
        <app-input-validator :errors="v$.lastName.$errors">
          <app-input
            v-model="state.lastName"
            label-text="Last Name"
            name="Last Name"
          ></app-input>
        </app-input-validator>
      </div>
      <div class="app-step-one-form-form__row">
        <app-input-validator :errors="v$.email.$errors">
          <app-input
            v-model="state.email"
            label-text="E-Mail"
            name="E-Mail"
            type="email"
            :disabled="authStore.isLoggedIn"
          ></app-input>
        </app-input-validator>

        <app-input-validator :errors="v$.phone.$errors">
          <app-intl-input
            id="phone"
            v-model="state.phone"
            placeholder="Telephone"
            :key="authStore.userData.phone"
          ></app-intl-input>
        </app-input-validator>
      </div>

      <app-button
        :disabled="isStartOrderLoading || loading"
        :loading="isStartOrderLoading || loading"
        type="submit"
        class="app-step-one-form-form__btn"
        theme="primary"
        >Start your press release order!</app-button
      >
    </form>
    <p v-if="isHome" class="app-step-one-form__descr">
      <b
        >Questions? If you have questions about placing an order, call us at: +1
        410-931-2966 (or +1 800-710-5535) 7AM-7PM Eastern Time Zone Monday
        through Friday.
      </b>
    </p>
    <p v-if="isHome" class="app-step-one-form__descr">
      By using this form you agree with the storage and handling of your data by
      this website. Your data will not be shared.
    </p>
    <p v-if="isHome" class="app-step-one-form__descr">
      All orders must be prepaid. We accept all major credit cards.
    </p>
  </div>
</template>

<script setup>
import { reactive, watch, computed, ref } from 'vue'
import { required, email, helpers } from '@vuelidate/validators'
import { useRoute, useRouter } from 'vue-router'
import { camelToSnake } from '@core/utils/helpers.js'
import { formatPrice } from '@core/utils/price'

import { useLoading } from '@core/composables/useLoading'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '@/stores/auth'
import { startOrder } from '@core/composables/order/startOrder'
import { useOrder } from '@core/composables/order/useOrder'

import StepProgress from '@modules/app-steps/step-progress.vue'

import AppInput from '@modules/ui/app-input.vue'
import AppButton from '@modules/ui/app-button.vue'
import AppInputValidator from '@modules/ui/app-input-validator.vue'
import AppIntlInput from '@modules/ui/app-intl-input.vue'
import ResumeOrder from './resume-order.vue'

import useVuelidate from '@vuelidate/core'
import useJwt from '@core/auth/jwt/useJwt'
import { getLoginRedirectQuery } from '@core/auth/utils'

const props = defineProps({
  packageItem: {
    type: Object,
  },
})

const firstPackageItem = computed(() => {
  return props?.packageItem?.packagePrices[0]
})

const firstPackageItemCredits = computed(() => firstPackageItem.value?.credits)

const packagePrices = computed(() => props?.packageItem?.packagePrices)
const packagePricesCount = computed(() => packagePrices?.value?.length)

const perReleasePrice = computed(() =>
  Math.round(firstPackageItem?.value?.price / firstPackageItemCredits.value)
)

const router = useRouter()
const route = useRoute()

const toast = useToast()
const authStore = useAuthStore()

const { jwt } = useJwt()

const { clearOrder } = useOrder()
const { isLoading: isStartOrderLoading, executeStartOrder } = startOrder()

const { loading, setLoading, resetLoading } = useLoading()
const ALREADY_TAKEN_EMAIL = 'The email has already been taken.'

const state = reactive({
  firstName: null,
  lastName: null,
  email: null,
  phone: null,
})

const minLengthWithTrim = (length) =>
  helpers.withMessage(
    `The field must contain at least ${length} characters`,
    (value) => {
      if (typeof value !== 'string') return false
      return value.trim().length >= length
    }
  )

const rules = {
  firstName: { required, minLength: minLengthWithTrim(1) },
  lastName: { required, minLength: minLengthWithTrim(1) },
  phone: {
    required,
    minLength: minLengthWithTrim(8),
  },

  email: {
    required,
    email,
  },
}

const isHome = computed(() => route.name === 'home')

const getClientInfo = () => {
  const { firstName, lastName, phone } = state

  const payload = { firstName, lastName, phone }

  if (props.specialPackageId) payload['packagePriceId'] = props.specialPackageId

  return camelToSnake(payload)
}

const v$ = useVuelidate(rules, state)

// Navigate off the awaited start-order result rather than a component-scoped
// watch(orderId): registering flips isLoggedIn, which makes step-one/index.vue
// swap this form for its loading bar and unmount it. A watcher would be disposed
// before /orders/start resolves, so navigation would be lost (the order is still
// created, which is why the resume banner then shows it). This runs to completion
// independently of the component, so it survives that remount.
const startOrderAndNavigate = async () => {
  const order = await executeStartOrder(getClientInfo())
  const id = order?.value?.id
  if (!id) return

  clearOrder()
  
  router.replace({ name: 'single-order', params: { id } })
}

const signUpUser = () => {
  setLoading()

  jwt
    .register(camelToSnake(state))
    .then(({ data: { token, client } }) => {
      jwt.setToken(token)
      authStore.setUser(client)
      startOrderAndNavigate()
    })
    .catch(({ error }) => {
      if (error === ALREADY_TAKEN_EMAIL) {
        return router.push({
          name: 'account-login',
          query: {
            email: state.email,
            tour: '1',
            ...getLoginRedirectQuery(route),
          },
        })
      }

      toast.error(error)
    })
    .finally(() => resetLoading())
}

const handleSubmit = async () => {
  const noErrors = await v$.value.$validate()
  if (!noErrors) return

  if (authStore.isLoggedIn) return startOrderAndNavigate()

  signUpUser()
}

watch(
  () => authStore.userData,
  (userData) => {
    state.firstName = userData.firstName
    state.lastName = userData?.lastName
    state.email = userData?.email
    state.phone = userData?.phone
  },
  { immediate: true }
)

watch(
  [() => authStore.isLoggedIn, () => route.query.resumeOrder],
  ([isLoggedIn, resumeOrder]) => {
    if (!isLoggedIn || !resumeOrder) return

    const orderId = String(resumeOrder).trim()

    if (!/^\d+$/.test(orderId)) {
      return
    }

    router.push({ name: 'single-order', params: { id: orderId } })
  },
  { immediate: true }
)
</script>
